import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, AlertTriangle, Wind, Sparkles } from 'lucide-react';

const RegasPage = () => {
  const whatWeOffer = [
    'R134a refrigerant regas for vehicles manufactured before 2017',
    'R1234yf refrigerant regas for newer vehicles',
    'System pressure testing before any regas',
    'Leak detection to identify faults',
    'Oil top-up included with regas service',
    'UV dye injection for future leak detection',
  ];

  const signs = [
    'Air blowing warm or not as cold as before',
    'Reduced airflow from vents',
    'Unpleasant smells when AC is running',
    'Unusual noises when air conditioning is on',
    'AC takes longer to cool the cabin',
  ];

  return (
    <div data-testid="regas-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">
              Air-Con Service
            </span>
            <h1 className="text-4xl font-bold text-slate-900 mt-3 mb-6">
              Air-Con Regas Service
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Professional refrigerant recharge service for R134a and R1234yf systems. We check your system thoroughly before any work is carried out.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
                data-testid="regas-cta-book"
              >
                Book a Regas
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:01323643333"
                className="inline-flex items-center gap-2 bg-white text-slate-700 px-6 py-3 rounded-lg font-medium border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <Phone className="w-4 h-4" />
                01323 643333
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">What We Offer</h2>
              <p className="text-slate-600 mb-6">
                A proper regas involves more than just adding refrigerant. We check your system to ensure there are no leaks or faults before any work begins.
              </p>
              <ul className="space-y-3">
                {whatWeOffer.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=600&q=80"
                alt="Air conditioning regas equipment"
                className="rounded-xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Signs You Need a Regas */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Signs Your Air-Con Needs Attention
            </h2>
            <p className="text-slate-600">
              If you notice any of these symptoms, it may be time to have your air conditioning checked.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {signs.map((sign) => (
              <div key={sign} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700 text-sm">{sign}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Optional Upgrades Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full mb-4">
                Optional Upgrades
              </span>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Recommended for Comfort & Air Quality
              </h2>
              <p className="text-slate-600">
                These optional services can be added to your air conditioning regas to improve cabin air quality and system hygiene. They are recommendations, not mandatory repairs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Cabin Filter Upgrade */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Wind className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <span className="text-xs text-teal-600 font-medium uppercase tracking-wider">Optional</span>
                    <h3 className="text-lg font-semibold text-slate-900">Cabin (Pollen) Filter Replacement</h3>
                  </div>
                </div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_eastbourne-cooling/artifacts/bf8n3d68_Image%201.jpeg"
                  alt="Dirty cabin filter showing trapped dust, pollen and debris"
                  className="w-full h-40 object-cover rounded-lg mb-4"
                />
                <p className="text-slate-600 text-sm leading-relaxed">
                  The cabin (pollen) filter cleans the air that enters the vehicle interior through the ventilation and air conditioning system. Over time it can become clogged with dust, pollen, road grime, and bacteria, which restricts airflow and reduces the effectiveness of the air conditioning. A blocked or dirty filter can also cause unpleasant smells and worsen allergies or respiratory irritation. Replacing the cabin filter helps the air conditioning work more efficiently, improves airflow, and ensures the air inside the vehicle stays clean and fresh.
                </p>
              </div>

              {/* Sanitiser Treatment Upgrade */}
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <span className="text-xs text-teal-600 font-medium uppercase tracking-wider">Optional</span>
                    <h3 className="text-lg font-semibold text-slate-900">Air Conditioning Sanitiser Treatment</h3>
                  </div>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  An air conditioning sanitiser treatment disinfects the internal components of the air conditioning and ventilation system, including the evaporator and air ducts. Moisture naturally builds up inside these areas, which can allow bacteria, mould, and fungi to grow over time. This is a common cause of musty or unpleasant smells when the air conditioning is switched on. The sanitiser treatment helps eliminate odours, improves air quality inside the cabin, and reduces bacteria circulating through the system.
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-slate-500 text-sm mb-4">
                Interested in adding these services? Just mention them when you enquire.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700"
              >
                Make an Enquiry
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 max-w-3xl mx-auto">
            <h3 className="text-xl font-semibold text-slate-900 mb-4">
              Why We Check Before We Regas
            </h3>
            <p className="text-slate-600 mb-4">
              Adding refrigerant to a system with a leak is wasteful, expensive, and environmentally harmful. We always check for leaks and faults first, so you know exactly what work is needed and why.
            </p>
            <p className="text-slate-600">
              This honest approach means you only pay for work that's actually needed.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Book?</h2>
          <p className="text-slate-300 mb-8">
            Get in touch for an honest assessment of your air conditioning system.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
            data-testid="regas-cta-bottom"
          >
            Request an Appointment
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RegasPage;
